/**
 * Raw fetch functions for org auth endpoints (yaas-api).
 * No React or Zustand dependencies — pure async functions.
 * All functions throw on non-OK responses.
 */

import { OAuthBrowserClient } from "@solomon-labs/oauth";

import type { MeResponse, OrgTokenSet } from "../types/orgAuth";

import { ORG_AUTH_GENERIC_FAILURE, OrgAuthRateLimitError, resolveOrgAuthUserMessage } from "./orgAuthMessages";

export {
    formatOrgAuthRateLimitMessage,
    getOrgAuthErrorMessage,
    isOrgAuthCredentialsError,
    ORG_AUTH_CREDENTIALS_FAILURE,
    ORG_AUTH_GENERIC_FAILURE,
    OrgAuthRateLimitError,
    resolveOrgAuthUserMessage
} from "./orgAuthMessages";
export type { ResolveOrgAuthMessageOptions } from "./orgAuthMessages";

const browserClients = new Map<string, OAuthBrowserClient>();

function getBrowserClient(config: OAuthConfig): OAuthBrowserClient {
    const cacheKey = `${config.apiBase}|${config.clientId}|${config.redirectUri}`;
    const existing = browserClients.get(cacheKey);
    if (existing) {
        return existing;
    }

    const client = new OAuthBrowserClient({
        tokenEndpoint: `${config.apiBase}/api/oauth/token` as never,
        revocationEndpoint: `${config.apiBase}/api/oauth/revoke` as never,
        clientId: config.clientId as never,
        redirectUri: config.redirectUri as never,
        issuer: config.apiBase,
        jwksUri: `${config.apiBase}/api/.well-known/jwks.json` as never
    });

    browserClients.set(cacheKey, client);
    return client;
}

export interface OAuthConfig {
    apiBase: string;
    clientId: string;
    redirectUri: string;
    scope: string;
    externalAuth?: {
        google?: {
            enabled: boolean;
            label?: string;
        };
        sso?: {
            enabled: boolean;
            label?: string;
        };
    };
}

export type ExternalAuthProvider = "google";

/**
 * Build the user-facing error for an auth-endpoint 429. `retryAfterSec`
 * comes from the response body (the `Retry-After` header is not exposed
 * cross-origin); older backends without the field get generic wait copy.
 */
async function parseRateLimitError(response: Response): Promise<OrgAuthRateLimitError> {
    const body = (await response.json().catch(() => ({}))) as {
        retryAfterSec?: unknown;
    };
    return new OrgAuthRateLimitError(typeof body.retryAfterSec === "number" ? body.retryAfterSec : null);
}

async function parseAuthError(
    response: Response,
    options: { appLabel?: string; fallback?: string } = {}
): Promise<Error> {
    const body = (await response.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        code?: string;
    };
    const userMessage =
        resolveOrgAuthUserMessage({
            code: body.error ?? body.code,
            message: body.message,
            appLabel: options.appLabel
        }) ??
        (options.fallback
            ? resolveOrgAuthUserMessage({
                  message: options.fallback,
                  appLabel: options.appLabel
              })
            : null) ??
        options.fallback ??
        ORG_AUTH_GENERIC_FAILURE;
    return new Error(userMessage);
}

/**
 * Self-serve forgot-password request. The backend responds success whether
 * or not the email matches an account (anti-enumeration), so a resolved
 * promise only means "request accepted", never "account exists". The reset
 * link is delivered exclusively by email.
 */
export async function requestOrgPasswordReset(apiBase: string, email: string): Promise<void> {
    let response: Response;
    try {
        response = await fetch(`${apiBase}/api/org/auth/forgot-password`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email })
        });
    } catch {
        throw new Error("Unable to reach the authentication server. Try again in a moment.");
    }

    if (response.status === 429) {
        throw await parseRateLimitError(response);
    }

    if (!response.ok) {
        throw new Error(`Password reset request failed: ${response.status}`);
    }
}

/**
 * Phase 1: Authenticate with email/password.
 *
 * POSTs credentials to authorize-login. The backend validates them, creates
 * a signed login_token, and returns the OAuth authorize URL as JSON.
 *
 * The caller must then navigate the browser to the returned URL (window.location.href)
 * rather than using fetch to follow the redirect chain. Cross-origin redirect chains
 * via fetch are blocked by browser CORS policy when the final destination is a static
 * SPA that does not serve Access-Control-Allow-Origin headers.
 */
export async function authorizeLogin(
    config: OAuthConfig,
    email: string,
    password: string,
    authorizeUrl: string,
    codeChallenge: string
): Promise<{ navigateTo: string }> {
    let response: Response;

    try {
        response = await fetch(`${config.apiBase}/api/org/auth/authorize-login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email,
                password,
                redirect_uri: authorizeUrl,
                client_id: config.clientId,
                code_challenge: codeChallenge
            })
        });
    } catch {
        throw new Error(
            "Unable to reach the authentication server. Make sure the API is running and the configured API URL is reachable."
        );
    }

    if (response.status === 401) {
        throw await parseAuthError(response, {
            fallback: "Invalid email or password."
        });
    }

    if (response.status === 403) {
        throw await parseAuthError(response);
    }

    if (response.status === 429) {
        throw await parseRateLimitError(response);
    }

    if (!response.ok) {
        throw await parseAuthError(response, {
            fallback: `Authentication failed: ${response.status}`
        });
    }

    const data = (await response.json()) as { url: string };
    return { navigateTo: data.url };
}

export async function startExternalLogin(
    config: OAuthConfig,
    provider: ExternalAuthProvider,
    authorizeUrl: string,
    codeChallenge: string,
    loginHint?: string
): Promise<{ navigateTo: string }> {
    if (provider !== "google") {
        throw new Error("Unsupported external login provider");
    }

    let response: Response;
    try {
        response = await fetch(`${config.apiBase}/api/org/auth/external/google/start`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                redirect_uri: authorizeUrl,
                client_id: config.clientId,
                code_challenge: codeChallenge,
                ...(loginHint ? { login_hint: loginHint } : {})
            })
        });
    } catch {
        throw new Error(
            "Unable to reach the authentication server. Make sure the API is running and the configured API URL is reachable."
        );
    }

    if (!response.ok) {
        throw await parseAuthError(response, {
            fallback: `Google login failed: ${response.status}`
        });
    }

    const data = (await response.json()) as { url: string };
    return { navigateTo: data.url };
}

export async function startSsoLogin(
    config: OAuthConfig,
    authorizeUrl: string,
    codeChallenge: string,
    loginHint?: string
): Promise<{ navigateTo: string }> {
    let response: Response;
    try {
        response = await fetch(`${config.apiBase}/api/org/auth/sso/start`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                redirect_uri: authorizeUrl,
                client_id: config.clientId,
                code_challenge: codeChallenge,
                ...(loginHint ? { email: loginHint, login_hint: loginHint } : {})
            })
        });
    } catch {
        throw new Error(
            "Unable to reach the authentication server. Make sure the API is running and the configured API URL is reachable."
        );
    }

    if (!response.ok) {
        throw await parseAuthError(response, {
            fallback: `SSO login failed: ${response.status}`
        });
    }

    const data = (await response.json()) as { url: string };
    return { navigateTo: data.url };
}

/**
 * Phase 3: Exchange an authorization code for tokens.
 */
export async function exchangeCode(
    config: OAuthConfig,
    code: string,
    codeVerifier: string,
    nonce: string
): Promise<OrgTokenSet> {
    const data = await getBrowserClient(config).exchangeCode({
        code,
        codeVerifier,
        nonce
    });

    return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        scope: data.scope
    };
}

/**
 * Refresh an access token using a refresh token.
 */
export async function refreshAccessToken(config: OAuthConfig, refreshToken: string): Promise<OrgTokenSet> {
    const data = await getBrowserClient(config).refreshToken({ refreshToken });

    return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        scope: data.scope
    };
}

/**
 * Revoke a refresh token (logout).
 */
export async function revokeToken(config: OAuthConfig, refreshToken: string): Promise<void> {
    await getBrowserClient(config).revokeToken(refreshToken);
}

/**
 * Returns a valid access token, refreshing silently if it expires within the
 * skew window (default 60 s). All concurrent callers share one refresh request.
 * Throws if refresh fails so callers can redirect to login.
 */
export async function ensureValidToken(
    config: OAuthConfig,
    tokens: OrgTokenSet,
    skewMs = 60_000
): Promise<{ accessToken: string; refreshed: false } | { accessToken: string; refreshed: true; tokens: OrgTokenSet }> {
    const result = await getBrowserClient(config).ensureValidToken(
        {
            ...tokens,
            scope: tokens.scope as never
        },
        skewMs
    );
    if (!result.refreshed) {
        return result;
    }

    return {
        accessToken: result.accessToken,
        refreshed: true,
        tokens: {
            accessToken: result.tokens.accessToken,
            refreshToken: result.tokens.refreshToken,
            expiresAt: result.tokens.expiresAt,
            scope: result.tokens.scope
        }
    };
}

/**
 * Fetch the current authenticated user's profile.
 */
export async function fetchMe(apiBase: string, accessToken: string): Promise<MeResponse> {
    const response = await fetch(`${apiBase}/api/org/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string; message?: string };
        throw new Error(
            resolveOrgAuthUserMessage({
                code: body.error,
                message:
                    body.message ??
                    `Failed to fetch user profile: ${response.status}${body.error ? ` — ${body.error}` : ""}`
            }) ?? ORG_AUTH_GENERIC_FAILURE
        );
    }

    const me = (await response.json()) as Pick<MeResponse, "user" | "org" | "orgRole"> &
        Partial<Omit<MeResponse, "user" | "org" | "orgRole">>;
    return {
        user: me.user,
        org: me.org,
        orgRole: me.orgRole,
        issuerOrg: me.issuerOrg ?? null,
        issuerCapability: me.issuerCapability ?? null,
        canActAsIssuer: me.canActAsIssuer ?? false,
        issuerCapabilityStatus: me.issuerCapabilityStatus ?? null,
        availableClientContexts: me.availableClientContexts ?? [],
        defaultClientContextUuid: me.defaultClientContextUuid ?? null
    };
}
